import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import { Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import 'react-big-calendar/lib/css/react-big-calendar.css';

import {
  useGetRoomsQuery,
  useGetBookingsQuery,
  useAddBookingMutation,
  useDeleteBookingMutation
} from '../../../redux/apis/ConferenceRoomApi';
import { useIsLoggedInQuery } from '../../../redux/apis/authApi';

import PageHeader from '../../../components/common/PageHeader';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import ManageRoomsModal from './ManageRoomsModal';
import { PageSkeleton } from '../../../components/UI/Skeleton/Skeleton';
import { notification } from '../../../utils/notification';
import { TOAST_TYPE } from '../../../utils/constant/errorMessage';
import { ROLES } from '../../../utils/constant/global';

import './RoomBooking.scss';

const { TOAST_SUCCESS, TOAST_ERROR } = TOAST_TYPE;
const { ADMIN, HR } = ROLES;

const localizer = momentLocalizer(moment);

const emptyForm = {
  roomId: '',
  title: '',
  purpose: '',
  attendees: '',
  date: '',
  startTime: '',
  endTime: ''
};

const RoomBooking = () => {
  const { data: loginData } = useIsLoggedInQuery();
  const currentUser = loginData?.data;
  const isAdminOrHr = [ADMIN, HR].includes(currentUser?.hrxRole?.roleName);

  const [selectedRoom, setSelectedRoom] = useState('');
  const [bookModal, setBookModal] = useState({ show: false });
  const [viewModal, setViewModal] = useState({ show: false, event: null });
  const [cancelModal, setCancelModal] = useState({ show: false, id: '' });
  const [manageRooms, setManageRooms] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: roomsData } = useGetRoomsQuery({});
  const rooms = roomsData?.data || [];
  // Rooms that can actually be booked (exclude maintenance / inactive).
  const bookableRooms = rooms.filter((r) => r.status !== 'Maintenance' && r.isActive !== false);

  const {
    data: bookingsData,
    isLoading,
    refetch: refetchBookings
  } = useGetBookingsQuery(selectedRoom ? { roomId: selectedRoom } : {});

  const [addBooking] = useAddBookingMutation();
  const [deleteBooking] = useDeleteBookingMutation();

  const {
    register,
    reset,
    handleSubmit,
    //setValue,
    formState: { errors }
  } = useForm({ defaultValues: emptyForm });

  const events = useMemo(() => {
    return (bookingsData?.data || []).map((b) => ({
      id: b._id,
      title: `${b.room?.name || 'Room'} — ${b.title}`,
      start: new Date(b.startTime),
      end: new Date(b.endTime),
      color: b.room?.color || '#4f46e5',
      raw: b
    }));
  }, [bookingsData]);

  const eventPropGetter = (event) => ({
    style: {
      backgroundColor: event.color,
      color: '#fff',
      border: 'none',
      borderRadius: '6px'
    }
  });

  // Open the booking modal, pre-filling the clicked slot.
  const handleSelectSlot = ({ start, end }) => {
    reset({
      ...emptyForm,
      roomId: selectedRoom || '',
      date: moment(start).format('YYYY-MM-DD'),
      startTime: moment(start).format('HH:mm'),
      endTime: moment(end).format('HH:mm')
    });
    setBookModal({ show: true });
  };

  const handleSelectEvent = (event) => {
    setViewModal({ show: true, event: event.raw });
  };

  const onSubmit = async (values) => {
    try {
      const startTime = moment(`${values.date} ${values.startTime}`, 'YYYY-MM-DD HH:mm').valueOf();
      const endTime = moment(`${values.date} ${values.endTime}`, 'YYYY-MM-DD HH:mm').valueOf();

      if (endTime <= startTime) {
        notification('End time must be after start time', TOAST_ERROR);
        return;
      }

      setSaving(true);
      const payload = {
        roomId: values.roomId,
        title: values.title,
        purpose: values.purpose || undefined,
        attendees: values.attendees ? Number(values.attendees) : undefined,
        startTime,
        endTime
      };
      const res = await addBooking(payload);
      if (res?.data?.success) {
        notification('Room booked successfully', TOAST_SUCCESS);
        setBookModal({ show: false });
        reset(emptyForm);
        refetchBookings();
      } else {
        // 409 conflict or validation error
        notification(res?.error?.data?.statusMessage || 'Could not book the room', TOAST_ERROR);
      }
    } catch (error) {
      notification(error?.message || 'Something went wrong', TOAST_ERROR);
    } finally {
      setSaving(false);
    }
  };

  const canCancel = (booking) =>
    isAdminOrHr || String(booking?.bookedBy) === String(currentUser?._id);

  const handleCancel = async () => {
    try {
      setCancelling(true);
      const res = await deleteBooking(cancelModal.id);
      if (res?.data?.success) {
        notification('Booking cancelled', TOAST_SUCCESS);
        refetchBookings();
      } else {
        notification(res?.error?.data?.statusMessage || 'Could not cancel', TOAST_ERROR);
      }
    } catch (error) {
      notification(error?.message, TOAST_ERROR);
    } finally {
      setCancelling(false);
      setCancelModal({ show: false, id: '' });
      setViewModal({ show: false, event: null });
    }
  };

  const openNewBooking = () => {
    reset({ ...emptyForm, roomId: selectedRoom || '' });
    setBookModal({ show: true });
  };

  const ev = viewModal.event;

  return (
    <div className="container-xxl room-booking">
      <PageHeader headerTitle="Conference Room Booking" />

      <div className="row clearfix g-3">
        <div className="col-sm-12">
          <div className="room-booking__card">
            <div className="room-booking__toolbar">
              <div className="room-booking__toolbar-left">
                <label className="room-booking__filter-label" htmlFor="room-booking-filter">
                  Room
                </label>
                <select
                  id="room-booking-filter"
                  className="form-select room-booking__room-filter"
                  value={selectedRoom}
                  onChange={(e) => setSelectedRoom(e.target.value)}
                >
                  <option value="">All rooms</option>
                  {rooms.map((r) => (
                    <option key={r._id} value={r._id}>
                      {r.name} ({r.capacity}){r.status === 'Maintenance' ? ' — Maintenance' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="room-booking__toolbar-actions">
                {isAdminOrHr && (
                  <button
                    type="button"
                    className="btn room-booking__ghost-btn"
                    onClick={() => setManageRooms(true)}
                  >
                    <i className="icofont-gear" aria-hidden="true" />
                    Manage rooms
                  </button>
                )}
                <button
                  type="button"
                  className="btn room-booking__add-btn"
                  onClick={openNewBooking}
                >
                  <i className="icofont-plus-circle" aria-hidden="true" />
                  Book a room
                </button>
              </div>
            </div>

            {rooms.length > 0 && (
              <div className="room-booking__legend">
                {rooms.map((r) => (
                  <span
                    key={r._id}
                    className={`room-booking__legend-item${
                      selectedRoom === r._id ? ' is-active' : ''
                    }${selectedRoom && selectedRoom !== r._id ? ' is-dim' : ''}`}
                  >
                    <span className="room-booking__legend-dot" style={{ background: r.color }} />
                    {r.name}
                    {r.status === 'Maintenance' && (
                      <i
                        className="icofont-tools-alt-2 room-booking__legend-maint"
                        title="Under maintenance"
                      />
                    )}
                  </span>
                ))}
              </div>
            )}

            <div className="room-booking__calendar">
              {isLoading ? (
                <PageSkeleton cards={1} rows={8} />
              ) : (
                <Calendar
                  localizer={localizer}
                  events={events}
                  selectable
                  views={['month', 'week', 'day']}
                  defaultView="week"
                  defaultDate={new Date()}
                  step={30}
                  timeslots={2}
                  style={{ height: '72vh' }}
                  onSelectSlot={handleSelectSlot}
                  onSelectEvent={handleSelectEvent}
                  eventPropGetter={eventPropGetter}
                  popup
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        centered
        className="rb-modal"
        dialogClassName="rb-modal__dialog"
        contentClassName="rb-modal__content"
        show={bookModal.show}
        onHide={() => setBookModal({ show: false })}
      >
        <Modal.Header className="rb-modal__header">
          <div className="rb-modal__header-content">
            <span className="rb-modal__header-icon" aria-hidden="true">
              <i className="icofont-calendar" />
            </span>
            <div className="rb-modal__header-copy">
              <Modal.Title className="rb-modal__title">Book a conference room</Modal.Title>
              <p className="rb-modal__subtitle">Choose a room and the time for this meeting</p>
            </div>
          </div>
          <button
            type="button"
            className="btn-close rb-modal__close"
            aria-label="Close"
            onClick={() => setBookModal({ show: false })}
          />
        </Modal.Header>
        <form className="rb-modal__form" onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body className="rb-modal__body">
            <div className="rb-modal__section">
              <div className="rb-modal__field">
                <label className="rb-modal__label" htmlFor="rb-room">
                  Room *
                </label>
                <select
                  id="rb-room"
                  className="form-select rb-modal__input"
                  {...register('roomId', { required: 'Please select a room' })}
                >
                  <option value="">Select a room...</option>
                  {bookableRooms.map((r) => (
                    <option key={r._id} value={r._id}>
                      {r.name} — seats {r.capacity} {r.location ? `(${r.location})` : ''}
                    </option>
                  ))}
                </select>
                {bookableRooms.length === 0 && (
                  <small className="rb-modal__hint">No rooms available to book right now.</small>
                )}
                {errors.roomId && (
                  <small className="text-danger rb-modal__error">{errors.roomId.message}</small>
                )}
              </div>

              <div className="rb-modal__field">
                <label className="rb-modal__label" htmlFor="rb-title">
                  Meeting title *
                </label>
                <input
                  id="rb-title"
                  className="form-control rb-modal__input"
                  placeholder="e.g. Sprint planning"
                  {...register('title', { required: 'Please enter a title' })}
                />
                {errors.title && (
                  <small className="text-danger rb-modal__error">{errors.title.message}</small>
                )}
              </div>

              <div className="rb-modal__grid rb-modal__grid--3">
                <div className="rb-modal__field">
                  <label className="rb-modal__label" htmlFor="rb-date">
                    Date *
                  </label>
                  <input
                    id="rb-date"
                    type="date"
                    className="form-control rb-modal__input"
                    {...register('date', { required: 'Required' })}
                  />
                  {errors.date && (
                    <small className="text-danger rb-modal__error">{errors.date.message}</small>
                  )}
                </div>
                <div className="rb-modal__field">
                  <label className="rb-modal__label" htmlFor="rb-start">
                    Start *
                  </label>
                  <input
                    id="rb-start"
                    type="time"
                    className="form-control rb-modal__input"
                    {...register('startTime', { required: 'Required' })}
                  />
                  {errors.startTime && (
                    <small className="text-danger rb-modal__error">
                      {errors.startTime.message}
                    </small>
                  )}
                </div>
                <div className="rb-modal__field">
                  <label className="rb-modal__label" htmlFor="rb-end">
                    End *
                  </label>
                  <input
                    id="rb-end"
                    type="time"
                    className="form-control rb-modal__input"
                    {...register('endTime', { required: 'Required' })}
                  />
                  {errors.endTime && (
                    <small className="text-danger rb-modal__error">{errors.endTime.message}</small>
                  )}
                </div>
              </div>

              <div className="rb-modal__grid">
                <div className="rb-modal__field">
                  <label className="rb-modal__label" htmlFor="rb-attendees">
                    Attendees
                  </label>
                  <input
                    id="rb-attendees"
                    type="number"
                    min="1"
                    className="form-control rb-modal__input"
                    placeholder="No. of people"
                    {...register('attendees')}
                  />
                </div>
                <div className="rb-modal__field">
                  <label className="rb-modal__label" htmlFor="rb-purpose">
                    Purpose
                  </label>
                  <input
                    id="rb-purpose"
                    className="form-control rb-modal__input"
                    placeholder="Optional"
                    {...register('purpose')}
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="rb-modal__footer">
            <button
              type="button"
              className="btn rb-modal__cancel"
              onClick={() => setBookModal({ show: false })}
            >
              Cancel
            </button>
            <button type="submit" className="btn rb-modal__submit" disabled={saving}>
              {saving ? (
                <span className="spinner-border spinner-border-sm" role="status" />
              ) : (
                <>
                  <i className="icofont-check-circled" aria-hidden="true" />
                  Confirm booking
                </>
              )}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal
        centered
        className="rb-modal"
        dialogClassName="rb-modal__dialog rb-modal__dialog--detail"
        contentClassName="rb-modal__content"
        show={viewModal.show}
        onHide={() => setViewModal({ show: false, event: null })}
      >
        <Modal.Header className="rb-modal__header">
          <div className="rb-modal__header-content">
            <span className="rb-modal__header-icon" aria-hidden="true">
              <i className="icofont-info-circle" />
            </span>
            <div className="rb-modal__header-copy">
              <Modal.Title className="rb-modal__title">Booking details</Modal.Title>
              <p className="rb-modal__subtitle">Room, time, and who booked this meeting</p>
            </div>
          </div>
          <button
            type="button"
            className="btn-close rb-modal__close"
            aria-label="Close"
            onClick={() => setViewModal({ show: false, event: null })}
          />
        </Modal.Header>
        <Modal.Body className="rb-modal__body">
          {ev && (
            <div className="rb-detail">
              <div className="rb-detail__hero">
                <span className="rb-detail__room" style={{ background: ev.room?.color }}>
                  {ev.room?.name || 'Room'}
                </span>
                <h5 className="rb-detail__title">{ev.title}</h5>
              </div>
              <div className="rb-detail__grid">
                <div className="rb-detail__item">
                  <span className="rb-detail__label">Date</span>
                  <span className="rb-detail__value">
                    {moment(ev.startTime).format('DD-MMM-YYYY')}
                  </span>
                </div>
                <div className="rb-detail__item">
                  <span className="rb-detail__label">Time</span>
                  <span className="rb-detail__value">
                    {moment(ev.startTime).format('hh:mm a')} –{' '}
                    {moment(ev.endTime).format('hh:mm a')}
                  </span>
                </div>
                <div className="rb-detail__item">
                  <span className="rb-detail__label">Booked by</span>
                  <span className="rb-detail__value">{ev.bookedByUser?.name || '-'}</span>
                </div>
                <div className="rb-detail__item">
                  <span className="rb-detail__label">Attendees</span>
                  <span className="rb-detail__value">{ev.attendees || '-'}</span>
                </div>
                {ev.purpose ? (
                  <div className="rb-detail__item rb-detail__item--full">
                    <span className="rb-detail__label">Purpose</span>
                    <span className="rb-detail__value">{ev.purpose}</span>
                  </div>
                ) : null}
                {ev.room?.location ? (
                  <div className="rb-detail__item rb-detail__item--full">
                    <span className="rb-detail__label">Location</span>
                    <span className="rb-detail__value">{ev.room.location}</span>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </Modal.Body>
        {ev && canCancel(ev) ? (
          <Modal.Footer className="rb-modal__footer">
            <button
              type="button"
              className="btn rb-modal__danger"
              onClick={() => setCancelModal({ show: true, id: ev._id })}
            >
              <i className="icofont-ui-delete" aria-hidden="true" />
              Cancel booking
            </button>
          </Modal.Footer>
        ) : null}
      </Modal>

      <ConfirmDialog
        show={cancelModal.show}
        onHide={() => setCancelModal({ show: false, id: '' })}
        onConfirm={handleCancel}
        loading={cancelling}
        variant="danger"
        icon="icofont-ui-delete"
        title="Cancel booking?"
        message="This will free up the room for the selected time. Are you sure?"
        confirmLabel="Cancel booking"
        cancelLabel="Keep"
      />

      {isAdminOrHr && <ManageRoomsModal show={manageRooms} onHide={() => setManageRooms(false)} />}
    </div>
  );
};

export default RoomBooking;
